import React from 'react';
import { FmButton } from '../FmButton';
import { FmInline, FmStack, FmText } from '../FmComposition';
import { FmHeading } from '../FmComposition';
import { FmDrawer } from '../FmDrawer';
import { FmSelect } from '../FmSelect';
import type {
  FmAnalysisAction,
  FmAnalysisSelector,
  FmAnalysisWorkspaceDescriptor,
} from '../FmAnalysisWorkspaceRoot.types';
import {
  analysisWorkspaceChromeActions,
  analysisWorkspaceEvidenceSelectors,
  analysisWorkspaceSecondarySelectors,
} from './analysisWorkspaceChrome';

function SelectorFields({
  selectors,
  onSelect,
  labeled,
  variant,
  idPrefix = 'analysis-selector',
}: {
  selectors: readonly FmAnalysisSelector[];
  onSelect: FmAnalysisWorkspaceDescriptor['onSelect'];
  labeled: boolean;
  variant?: 'outlined' | 'borderless';
  /** The refine sheet mounts a second copy of the controls; ids stay unique. */
  idPrefix?: string;
}) {
  return (
    <>
      {selectors.map((selector) => (
        <span key={selector.id} title={selector.disabledReason}>
          <FmSelect
            id={`${idPrefix}-${selector.id}`}
            label={labeled ? selector.label : undefined}
            ariaLabel={selector.label}
            value={selector.value}
            options={selector.options.map((option) => ({
              value: option.id,
              label: option.label,
            }))}
            disabled={Boolean(selector.disabledReason)}
            virtual={false}
            onChange={(value) => onSelect(selector.id, String(value))}
            controlWidth="md"
            variant={variant}
          />
        </span>
      ))}
    </>
  );
}

export function AnalysisWorkspaceActionButtons({
  actions,
  onAction,
  secondaryVariant = 'bordered',
}: {
  actions: readonly FmAnalysisAction[];
  onAction: FmAnalysisWorkspaceDescriptor['onAction'];
  /** Use `default` on an over-map plate so the plate owns the only visible border. */
  secondaryVariant?: 'bordered' | 'default';
}) {
  return (
    <>
      {actions.map((action) => (
        <FmButton
          key={action.id}
          variant={action.intent === 'primary' ? 'primary' : secondaryVariant}
          disabled={Boolean(action.disabledReason)}
          title={action.disabledReason}
          onClick={() => onAction(action.id)}
        >
          {action.label}
        </FmButton>
      ))}
    </>
  );
}

export function AnalysisWorkspaceFilters({
  descriptor,
}: {
  descriptor: FmAnalysisWorkspaceDescriptor;
}) {
  const selectors = analysisWorkspaceSecondarySelectors(descriptor);
  if (selectors.length === 0) return null;
  return (
    <FmInline
      gap="sm"
      wrap
      className="fm-analysis-workspace__filters"
      data-testid="analysis-workspace-filters"
    >
      <SelectorFields
        selectors={selectors}
        onSelect={descriptor.onSelect}
        labeled={false}
        variant="borderless"
      />
    </FmInline>
  );
}

export function AnalysisWorkspaceEvidenceFilters({
  descriptor,
}: {
  descriptor: FmAnalysisWorkspaceDescriptor;
}) {
  const selectors = analysisWorkspaceEvidenceSelectors(descriptor);
  if (selectors.length === 0) return null;
  return (
    <FmStack gap="sm" data-testid="analysis-workspace-evidence-filters">
      <SelectorFields selectors={selectors} onSelect={descriptor.onSelect} labeled />
    </FmStack>
  );
}

/**
 * Below the desktop tier the header's selectors and actions would stack into a
 * wall of controls above the map; they collapse into one bottom sheet behind a
 * single trigger instead. CSS shows the trigger only at that tier.
 */
export function AnalysisWorkspaceRefineSheet({
  descriptor,
  summary,
  selectors,
  actions,
}: {
  descriptor: FmAnalysisWorkspaceDescriptor;
  summary?: string;
  selectors: readonly FmAnalysisSelector[];
  actions: readonly FmAnalysisAction[];
}) {
  const [open, setOpen] = React.useState(false);
  if (selectors.length === 0 && actions.length === 0) return null;
  return (
    <div className="fm-analysis-workspace__refine">
      <FmButton
        variant="bordered"
        size="sm"
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => setOpen(true)}
        data-testid="analysis-workspace-refine-trigger"
      >
        {descriptor.copy.refineControls}
      </FmButton>
      <FmDrawer
        open={open}
        onClose={() => setOpen(false)}
        title={descriptor.copy.refineControls}
        closeLabel={descriptor.copy.closeRefineControls}
        placement="bottom"
        data-testid="analysis-workspace-refine-sheet"
      >
        <FmStack gap="md">
          {summary ? <FmText tone="muted">{summary}</FmText> : null}
          <SelectorFields
            selectors={selectors}
            onSelect={descriptor.onSelect}
            labeled
            idPrefix="analysis-sheet-selector"
          />
          {actions.length > 0 ? (
            <FmInline gap="sm" wrap>
              <AnalysisWorkspaceActionButtons
                actions={actions}
                onAction={(id) => {
                  // An action may open a dialog; the sheet closes first so the
                  // dialog never stacks under it.
                  setOpen(false);
                  descriptor.onAction(id);
                }}
              />
            </FmInline>
          ) : null}
        </FmStack>
      </FmDrawer>
    </div>
  );
}

/** The media plate's selectors and chrome actions, gathered for the mobile sheet. */
export function AnalysisWorkspaceSeriesRefineSheet({
  descriptor,
}: {
  descriptor: FmAnalysisWorkspaceDescriptor;
}) {
  return (
    <AnalysisWorkspaceRefineSheet
      descriptor={descriptor}
      selectors={analysisWorkspaceSecondarySelectors(descriptor)}
      actions={analysisWorkspaceChromeActions(descriptor)}
    />
  );
}

export function AnalysisWorkspaceHeader({
  descriptor,
}: {
  descriptor: FmAnalysisWorkspaceDescriptor;
}) {
  return (
    <header className="fm-analysis-workspace__header">
      <FmStack gap="xs">
        <FmText eyebrow tone="muted">
          {descriptor.eyebrow}
        </FmText>
        <FmHeading level={1}>{descriptor.title}</FmHeading>
        <FmText className="fm-analysis-workspace__lede" tone="muted">
          {descriptor.summary}
        </FmText>
      </FmStack>
      <FmInline gap="sm" wrap className="fm-analysis-workspace__header-controls">
        <SelectorFields
          selectors={descriptor.selectors}
          onSelect={descriptor.onSelect}
          labeled
        />
        <AnalysisWorkspaceActionButtons
          actions={descriptor.actions}
          onAction={descriptor.onAction}
        />
      </FmInline>
      <AnalysisWorkspaceRefineSheet
        descriptor={descriptor}
        summary={descriptor.summary}
        selectors={descriptor.selectors}
        actions={descriptor.actions}
      />
    </header>
  );
}
